/**
 * Mirrors apps/frontend/src/lib/upload-template-to-execution-storage.ts for Word add-in.
 * Copies catalog template files into execution-templates/{executionTemplateId}/...
 */
import { supabase } from '../../../core/lib/supabase';

export const BLANK_TEMPLATE_ID = '00000000-0000-0000-0000-000000000000';
const BLANK_CONTENT_PATH = '00000000-0000-0000-0000-000000000000/template.docx';
export const BLANK_EXCEL_TEMPLATE_ID = '00000000-0000-0000-0000-000000000001';
const BLANK_EXCEL_CONTENT_PATH = '00000000-0000-0000-0000-000000000001/template.xlsx';
const EXECUTION_TEMPLATES_BUCKET = 'execution-templates';
const TEMPLATES_BUCKET = 'templates';

function generateTimestampFilename(ext: '.docx' | '.xlsx'): string {
  const iso = new Date().toISOString().replace(/[:.]/g, '-');
  return `${iso}${ext}`;
}

/** Display name for `workpill_execution_templates.name` from an uploaded file (basename without extension). */
export function getExecutionTemplateDisplayNameFromFile(file: File): string {
  const raw = file.name.replace(/[/\\]/g, '');
  const withoutExt = raw.replace(/\.(docx|xlsx)$/i, '').trim();
  return withoutExt || 'Template';
}

/**
 * Uploads a user-selected .docx or .xlsx to execution-templates/{executionTemplateId}/{timestamp}.ext
 * (same naming pattern as catalog seed uploads).
 */
export async function uploadUserFileToExecutionTemplateStorage(params: {
  executionTemplateId: string;
  file: File;
}): Promise<string> {
  const lower = params.file.name.toLowerCase();
  let ext: '.docx' | '.xlsx';
  let contentType: string;
  if (lower.endsWith('.docx')) {
    ext = '.docx';
    contentType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  } else if (lower.endsWith('.xlsx')) {
    ext = '.xlsx';
    contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  } else {
    throw new Error('Only .docx and .xlsx files are allowed.');
  }

  const filename = generateTimestampFilename(ext);
  const storagePath = `${params.executionTemplateId}/${filename}`;

  const { error } = await supabase.storage
    .from(EXECUTION_TEMPLATES_BUCKET)
    .upload(storagePath, params.file, { contentType, upsert: false });

  if (error) throw new Error(`Failed to upload template to storage: ${error.message}`);

  return storagePath;
}

async function downloadTemplateBlob(contentPath: string): Promise<Blob> {
  if (contentPath.startsWith('http')) {
    const response = await fetch(contentPath);
    if (!response.ok) throw new Error(`Failed to download template: ${response.statusText}`);
    return response.blob();
  }

  const { data, error } = await supabase.storage.from(TEMPLATES_BUCKET).download(contentPath);
  if (error) throw new Error(`Failed to download template: ${error.message}`);
  if (!data) throw new Error('Template download returned empty');
  return data;
}

export interface UploadTemplateToExecutionStorageParams {
  executionTemplateId: string;
  templateId: string;
  contentUrl?: string | null;
}

function isExcelTemplate(templateId: string, contentPath: string): boolean {
  if (templateId === BLANK_EXCEL_TEMPLATE_ID) return true;
  const lower = contentPath.toLowerCase();
  return lower.endsWith('.xlsx') || lower.endsWith('.xls') || lower.endsWith('.csv');
}

export async function uploadTemplateToExecutionStorage({
  executionTemplateId,
  templateId,
  contentUrl,
}: UploadTemplateToExecutionStorageParams): Promise<string> {
  let contentPath: string;

  if (contentUrl?.trim()) {
    contentPath = contentUrl.trim();
  } else if (templateId === BLANK_TEMPLATE_ID) {
    contentPath = BLANK_CONTENT_PATH;
  } else if (templateId === BLANK_EXCEL_TEMPLATE_ID) {
    contentPath = BLANK_EXCEL_CONTENT_PATH;
  } else {
    const { data, error } = await supabase
      .from('templates')
      .select('content_url')
      .eq('id', templateId)
      .maybeSingle();

    if (error) throw new Error(`Failed to fetch template: ${error.message}`);
    if (!data?.content_url) {
      throw new Error(`Template ${templateId} has no content_url (file not uploaded)`);
    }
    contentPath = data.content_url;
  }

  const blob = await downloadTemplateBlob(contentPath);
  const isExcel = isExcelTemplate(templateId, contentPath);
  const ext: '.docx' | '.xlsx' = isExcel ? '.xlsx' : '.docx';
  const filename = generateTimestampFilename(ext);
  const storagePath = `${executionTemplateId}/${filename}`;

  const contentType = isExcel
    ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

  const { error: upErr } = await supabase.storage
    .from(EXECUTION_TEMPLATES_BUCKET)
    .upload(storagePath, blob, { contentType });

  if (upErr) throw new Error(`Failed to upload template to storage: ${upErr.message}`);

  return storagePath;
}

/** User-provided .docx for a new execution template row (blank project + upload). */
export async function uploadUserDocxToExecutionTemplate(
  executionTemplateId: string,
  blob: Blob
): Promise<string> {
  const filename = generateTimestampFilename('.docx');
  const storagePath = `${executionTemplateId}/${filename}`;
  const { error } = await supabase.storage.from(EXECUTION_TEMPLATES_BUCKET).upload(storagePath, blob, {
    contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
  if (error) throw new Error(`Failed to upload document: ${error.message}`);
  return storagePath;
}
